import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { Calendar, ChevronDown } from 'lucide-react';

// --- Mock Data ---
const generateData = (days) => {
    const data = [];
    const today = new Date();
    for (let i = days; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        data.push({
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            sales: Math.floor(Math.random() * 5000) + 1000,
            revenue: Math.floor(Math.random() * 3000) + 500,
            profit: Math.floor(Math.random() * 1000) + 100,
        });
    }
    return data;
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const ChartWidget = ({ data: externalData, title = "Performance Analytics" }) => {
    const [chartType, setChartType] = useState('Bar');
    const [dateRange, setDateRange] = useState('7'); // days
    const [isTypeOpen, setIsTypeOpen] = useState(false);
    const [isRangeOpen, setIsRangeOpen] = useState(false);

    // Dynamic Data Generation based on filter (only if externalData is not provided)
    const internalData = useMemo(() => generateData(parseInt(dateRange)), [dateRange]);
    const data = externalData || internalData;

    // --- Renderers ---
    const renderChart = () => {
        switch (chartType) {
            case 'Line':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                itemStyle={{ color: '#1e293b' }}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="sales" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'Pie':
                // Aggregate data for Pie chart
                const pieData = [
                    { name: 'Sales', value: data.reduce((acc, curr) => acc + curr.sales, 0) },
                    { name: 'Revenue', value: data.reduce((acc, curr) => acc + curr.revenue, 0) },
                    { name: 'Profit', value: data.reduce((acc, curr) => acc + curr.profit, 0) },
                ];
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );
            case 'Bar':
            default:
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                            <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                            <Tooltip
                                cursor={{ fill: '#f1f5f9' }}
                                contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                            />
                            <Legend />
                            <Bar dataKey="sales" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={30} />
                            <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} barSize={30} />
                        </BarChart>
                    </ResponsiveContainer>
                );
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto p-4 font-sans">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">

                {/* Header & Filters */}
                <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">{title}</h2>
                        <p className="text-sm text-slate-500">Overview of key metrics over time</p>
                    </div>

                    <div className="flex gap-3">
                        {/* Chart Type Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsTypeOpen(!isTypeOpen)}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-sm font-medium transition-colors border border-slate-200"
                            >
                                {chartType} Chart
                                <ChevronDown size={16} className={`transition-transform ${isTypeOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isTypeOpen && (
                                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-slate-100 z-10 py-1">
                                    {['Bar', 'Line', 'Pie'].map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => {
                                                setChartType(type);
                                                setIsTypeOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${chartType === type ? 'text-blue-600 font-medium bg-blue-50' : 'text-slate-600'}`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Date Range Dropdown - Only show if using internal data */}
                        {!externalData && (
                            <div className="relative">
                                <button
                                    onClick={() => setIsRangeOpen(!isRangeOpen)}
                                    className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-sm font-medium transition-colors border border-slate-200"
                                >
                                    <Calendar size={16} className="text-slate-500" />
                                    Last {dateRange} Days
                                    <ChevronDown size={16} className={`transition-transform ${isRangeOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isRangeOpen && (
                                    <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-100 z-10 py-1">
                                        {[
                                            { label: 'Last 7 Days', value: '7' },
                                            { label: 'Last 14 Days', value: '14' },
                                            { label: 'Last 30 Days', value: '30' },
                                        ].map((range) => (
                                            <button
                                                key={range.value}
                                                onClick={() => {
                                                    setDateRange(range.value);
                                                    setIsRangeOpen(false);
                                                }}
                                                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${dateRange === range.value ? 'text-blue-600 font-medium bg-blue-50' : 'text-slate-600'}`}
                                            >
                                                {range.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Chart Area */}
                <div className="p-6 bg-slate-50/50">
                    {renderChart()}
                </div>

                {/* Footer Stats (Optional Polish) */}
                <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 bg-white">
                    <div className="p-4 text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Total Sales</p>
                        <p className="text-lg font-bold text-slate-800 mt-1">
                            ${data.reduce((acc, curr) => acc + curr.sales, 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="p-4 text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Revenue</p>
                        <p className="text-lg font-bold text-green-600 mt-1">
                            +${data.reduce((acc, curr) => acc + curr.revenue, 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="p-4 text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Profit</p>
                        <p className="text-lg font-bold text-blue-600 mt-1">
                            +${data.reduce((acc, curr) => acc + curr.profit, 0).toLocaleString()}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

ChartWidget.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            date: PropTypes.string.isRequired,
            sales: PropTypes.number.isRequired,
            revenue: PropTypes.number.isRequired,
            profit: PropTypes.number.isRequired,
        })
    ),
    title: PropTypes.string,
};

export default ChartWidget;
